import React, { useEffect, useRef, useState } from "react";
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import {
    FaArrowRight,
    FaCalendarAlt,
    FaChartBar,
    FaHouseUser,
    FaHospitalUser,
    FaNotesMedical,
    FaRegClock
} from 'react-icons/fa';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';
import 'moment/locale/pt-br';
import { Link } from 'react-router-dom';
import PortalSpinner from '../../components/Spinner';
import './index.css';
import { partnersService } from '../../services/partners';
import { patientsService } from '../../services/patients';
import { pointService } from '../../services/pointControl';
import { scheduleService } from '../../services/schedule';
import { sessionService } from '../../services/session';
import {
    canAccessEvolutions,
    canAccessPatients,
    canAccessPoint,
    canManagePatients,
    canAccessReports,
    canViewPartners,
    canViewSchedule,
    getAccessLabel,
    isFuncionario
} from '../../utils/access';
import { formatDateDisplay } from '../../utils/date';
import { filterNamedPartners, filterNamedPatients } from '../../utils/records';

const acesso = Number(sessionStorage.getItem("tipoAcesso") || 0);

const createDefaultFilters = () => ({
    dataDe: moment().startOf('month').format('YYYY-MM-DD'),
    dataAte: moment().format('YYYY-MM-DD'),
    idParceiro: ''
});

const Home = () => {
    const messageTimer = useRef(null);
    const [show, setShow] = useState(false);
    const [message, setMessage] = useState('');
    const [loadingSummary, setLoadingSummary] = useState(true);
    const [partnerList, setPartnerList] = useState([]);
    const [patientList, setPatientList] = useState([]);
    const [todaySchedules, setTodaySchedules] = useState([]);
    const [todayPoints, setTodayPoints] = useState([]);
    const [filters, setFilters] = useState(createDefaultFilters);
    const [modalShowPaciente, setModalShowPaciente] = useState(false);
    const [modalShowParceiro, setModalShowParceiro] = useState(false);

    const showMessage = (text) => {
        clearTimeout(messageTimer.current);
        setMessage(text);
        setShow(true);
        messageTimer.current = setTimeout(() => setShow(false), 7000);
    };

    useEffect(() => () => clearTimeout(messageTimer.current), []);

    useEffect(() => {
        const fetchDashboard = async () => {
            const currentDay = moment().format('YYYYMMDD');
            const patientRequest = canManagePatients(acesso) ? patientsService.get() : patientsService.getAssigned();

            try {
                setLoadingSummary(true);

                const [partnerResult, patientResult, scheduleResult, pointResult] = await Promise.allSettled([
                    partnersService.getList(),
                    patientRequest,
                    canViewSchedule(acesso) ? scheduleService.getByDay(currentDay) : Promise.resolve({ data: [] }),
                    canAccessPoint(acesso) ? pointService.getByDay(currentDay) : Promise.resolve({ data: [] })
                ]);

                const nextPartnerList = partnerResult.status === 'fulfilled' ? filterNamedPartners(partnerResult.value.data || []) : [];
                const nextPatientList = patientResult.status === 'fulfilled' ? filterNamedPatients(patientResult.value.data || []) : [];
                const nextScheduleList = scheduleResult.status === 'fulfilled' ? (scheduleResult.value.data || []) : [];
                const nextPointList = pointResult.status === 'fulfilled' ? (pointResult.value.data || []) : [];

                setPartnerList(nextPartnerList);
                setPatientList(nextPatientList);
                setTodaySchedules(nextScheduleList);
                setTodayPoints(nextPointList);

                const hasPartialError = [partnerResult, patientResult, scheduleResult, pointResult]
                    .some((result) => result.status === 'rejected');

                if (hasPartialError) {
                    showMessage('Parte do resumo nao foi carregada, mas o portal continuou operando.');
                }
            } catch (error) {
                showMessage(error.message || 'Nao foi possivel carregar o resumo inicial do portal.');
            } finally {
                setLoadingSummary(false);
            }
        };

        fetchDashboard();
    }, []);

    const validate = () => {
        if (!filters.dataDe) {
            showMessage('Data de inicio vazia.');
            return false;
        }

        if (!filters.dataAte) {
            showMessage('Data final vazia.');
            return false;
        }

        return true;
    };

    const cleanFilters = () => {
        setFilters(createDefaultFilters());
    };

    const exportPdf = (fileName, head, body) => {
        const doc = new jsPDF({ orientation: 'landscape' });
        autoTable(doc, {
            head: [head],
            body
        });
        doc.save(fileName);
    };

    const generateReportPatient = async () => {
        try {
            if (!validate()) {
                return;
            }

            const dataAg = moment().format('YYYYMMDD');
            const { data } = await scheduleService.getReportPatient({
                dataDe: moment(filters.dataDe).format('YYYYMMDD'),
                dataAte: moment(filters.dataAte).format('YYYYMMDD'),
                idParceiro: filters.idParceiro || null
            });

            if (!data || !data.length) {
                throw new Error('Nao foram encontrados registros.');
            }

            let valorTotal = 0;
            const datatable = data.map((item) => {
                valorTotal += item.ValorUtilizado;
                return [
                    item.Nome_Parceiro,
                    item.Nome_Paciente,
                    formatDateDisplay(item.dataAgendamento),
                    item.entrada,
                    item.saida,
                    item.Ativo,
                    item.Email,
                    item.ValorHora,
                    item.hourDiff,
                    item.ValorUtilizado
                ];
            });

            datatable.push(['Parceiro', 'Paciente', 'Data', 'Entrada', 'Saida', 'Ativo?', 'Email', 'Valor/Hora', 'Tempo Utilizado', valorTotal]);
            exportPdf(`${dataAg}_Relatorio_Pacientes.pdf`, ['Parceiro', 'Paciente', 'Data', 'Entrada', 'Saida', 'Ativo?', 'Email', 'Valor/Hora', 'Tempo Utilizado', 'Soma Total'], datatable);
        } catch (error) {
            showMessage(error.message);
        }
    };

    const timeToMinutes = (timeString) => {
        const [hours, minutes] = `${timeString || '00:00'}`.split(':').map(Number);
        return (hours || 0) * 60 + (minutes || 0);
    };

    const generateReportPartner = async () => {
        try {
            if (!validate()) {
                return;
            }

            const dataAg = moment().format('YYYYMMDD');
            const { data } = await scheduleService.getReportPartner({
                dataDe: moment(filters.dataDe).format('YYYYMMDD'),
                dataAte: moment(filters.dataAte).format('YYYYMMDD'),
                idParceiro: filters.idParceiro || null
            });

            if (!data || !data.length) {
                throw new Error('Nao foram encontrados registros.');
            }

            let valorTotal = 0;
            let horasTotais = 0;
            const datatable = data.map((item) => {
                valorTotal += item.ValorUtilizado;
                horasTotais += timeToMinutes(item.hourDiff);

                return [
                    item.Nome_Parceiro,
                    item.sala,
                    formatDateDisplay(item.dataChegada),
                    item.entrada,
                    item.saida,
                    item.Ativo,
                    item.Email,
                    item.ValorHora,
                    item.hourDiff,
                    item.ValorUtilizado
                ];
            });

            const totalHours = Math.floor(horasTotais / 60);
            const totalMinutes = horasTotais % 60;
            const formattedTime = `${String(totalHours).padStart(2, '0')}:${String(Math.floor(totalMinutes)).padStart(2, '0')}`;
            datatable.push(['Parceiro', 'Sala', 'Data', 'Entrada', 'Saida', 'Ativo?', 'Email', 'Valor/Hora', formattedTime, valorTotal]);

            exportPdf(`${dataAg}_Relatorio_Parceiros.pdf`, ['Parceiro', 'Sala', 'Data', 'Entrada', 'Saida', 'Ativo?', 'Email', 'Valor/Hora', 'Tempo Total', 'Soma Total'], datatable);
        } catch (error) {
            showMessage(error.message);
        }
    };

    const summaryCards = [
        {
            title: 'Profissionais',
            value: partnerList.length,
            icon: <FaHospitalUser />,
            description: 'Equipe carregada no portal'
        },
        {
            title: canManagePatients(acesso) ? 'Pacientes' : 'Sua carteira',
            value: patientList.length,
            icon: <FaHouseUser />,
            description: canManagePatients(acesso) ? 'Cadastros visiveis para este perfil' : 'Pacientes disponiveis para atendimento'
        },
        {
            title: 'Agenda hoje',
            value: todaySchedules.length,
            icon: <FaCalendarAlt />,
            description: 'Atendimentos previstos para o dia'
        },
        {
            title: 'Ponto hoje',
            value: todayPoints.length,
            icon: <FaRegClock />,
            description: 'Registros operacionais do dia'
        }
    ];

    const quickActions = [
        canViewSchedule(acesso) ? {
            id: 'schedule',
            title: 'Abrir agenda',
            text: isFuncionario(acesso) ? 'Consultar sua agenda em leitura.' : 'Ver e ajustar os atendimentos do dia.',
            icon: <FaCalendarAlt />,
            to: '/schedule'
        } : null,
        canAccessPoint(acesso) ? {
            id: 'point',
            title: 'Registrar ponto',
            text: isFuncionario(acesso) ? 'Lancar sua entrada e saida.' : 'Controlar a operacao de ponto.',
            icon: <FaRegClock />,
            to: '/Partnerschedule'
        } : null,
        canAccessEvolutions(acesso) ? {
            id: 'evolution',
            title: 'Registrar evolucao',
            text: 'Ir direto para o prontuario clinico.',
            icon: <FaNotesMedical />,
            to: '/evolution'
        } : null,
        canAccessPatients(acesso) ? {
            id: 'patient',
            title: canManagePatients(acesso) ? 'Gestao de pacientes' : 'Carteira de pacientes',
            text: canManagePatients(acesso) ? 'Atualizar cadastro e vinculos.' : 'Consultar pacientes em leitura.',
            icon: <FaHouseUser />,
            to: '/patient'
        } : null,
        (canViewPartners(acesso) || canAccessReports(acesso)) ? {
            id: 'partner',
            title: canAccessReports(acesso) ? 'Equipe e acessos' : 'Profissionais',
            text: canAccessReports(acesso)
                ? 'Administrar usuarios, perfis e habilitacoes.'
                : 'Consultar a equipe em leitura.',
            icon: <FaHospitalUser />,
            to: '/partner'
        } : null
    ].filter(Boolean);

    const completedSchedules = todaySchedules.filter((item) => item.saida).length;
    const openSchedules = Math.max(todaySchedules.length - completedSchedules, 0);
    const completedPoints = todayPoints.filter((item) => item.saida).length;
    const todayLabel = moment().locale('pt-br').format('dddd, DD [de] MMMM');

    return (
        <Container fluid className="custom-background">
            <div className="portal-alert">
                <Alert show={show} variant="info" dismissible onClose={() => setShow(false)}>
                    {message}
                </Alert>
            </div>

            <div className="home-dashboard">
                <section className="home-hero">
                    <div className="home-hero-panel primary">
                        <span className="home-highlight">Painel inicial da clinica</span>
                        <h1 className="page-title">{`Bem-vinda, ${sessionService.getDisplayName()}`}</h1>
                        <p className="home-hero-copy">
                            {`Hoje e ${todayLabel}.`}
                        </p>

                        <div className="home-chip-list">
                            <span className="home-chip">{getAccessLabel(acesso)}</span>
                            <span className="home-chip">{`${completedSchedules} atendimentos concluidos`}</span>
                            <span className="home-chip">{`${openSchedules} pendentes na agenda`}</span>
                            <span className="home-chip">{`${completedPoints} saidas registradas`}</span>
                        </div>
                    </div>

                    <div className="home-hero-panel secondary">
                        <div className="home-panel-heading">
                            <div>
                                <span className="home-section-label">Leitura rapida</span>
                                <h4>Situacao do dia</h4>
                            </div>
                        </div>

                        {loadingSummary ? (
                            <PortalSpinner
                                compact
                                label="Montando resumo"
                                description="Conferindo agenda, pacientes e operacao do dia."
                            />
                        ) : null}

                        <div className="home-stat-list">
                            <div className="home-stat-item">
                                <strong>Agenda em andamento</strong>
                                <span>{todaySchedules.length ? `${todaySchedules.length} registros carregados para hoje.` : 'Nenhum atendimento encontrado para hoje.'}</span>
                            </div>
                            <div className="home-stat-item">
                                <strong>Operacao da equipe</strong>
                                <span>{todayPoints.length ? `${todayPoints.length} marcacoes de ponto ja registradas.` : 'Sem registros de ponto carregados para hoje.'}</span>
                            </div>
                            {canAccessEvolutions(acesso) ? (
                                <div className="home-stat-item">
                                    <strong>Prontuario clinico</strong>
                                    <span>Seu perfil pode consultar e registrar evolucoes conforme a carteira autorizada.</span>
                                </div>
                            ) : (
                                <div className="home-stat-item">
                                    <strong>Escopo do Cadastro</strong>
                                    <span>Este perfil opera agenda, pacientes e ponto, sem acesso ao conteudo clinico das evolucoes.</span>
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                <div className="home-summary-grid">
                    {summaryCards.map((item) => (
                        <Card className="home-summary-card" key={item.title}>
                            <Card.Body>
                                <div className="home-summary-top">
                                    <span className="home-summary-icon">{item.icon}</span>
                                    <span className="home-section-label">{item.title}</span>
                                </div>
                                <strong className="home-summary-value">
                                    {loadingSummary ? '--' : item.value}
                                </strong>
                                <p className="home-summary-text">{item.description}</p>
                            </Card.Body>
                        </Card>
                    ))}
                </div>

                <div className="home-main-grid">
                    <Card className="home-panel-card">
                        <Card.Body>
                            <div className="home-panel-heading">
                                <div>
                                    <span className="home-section-label">Atalhos</span>
                                    <h3>Fluxos principais</h3>
                                </div>
                            </div>

                            <div className="home-shortcut-grid">
                                {quickActions.map((item) => (
                                    <Link className="home-shortcut-card" key={item.id} to={item.to}>
                                        <span className="home-shortcut-icon">{item.icon}</span>
                                        <div>
                                            <strong>{item.title}</strong>
                                            <p>{item.text}</p>
                                        </div>
                                        <FaArrowRight className="home-shortcut-arrow" />
                                    </Link>
                                ))}

                                {canAccessReports(acesso) ? (
                                    <button className="home-shortcut-card action" onClick={() => setModalShowPaciente(true)} type="button">
                                        <span className="home-shortcut-icon"><FaChartBar /></span>
                                        <div>
                                            <strong>Relatorio pacientes</strong>
                                            <p>Exportar resumo financeiro e operacional.</p>
                                        </div>
                                        <FaArrowRight className="home-shortcut-arrow" />
                                    </button>
                                ) : null}

                                {canAccessReports(acesso) ? (
                                    <button className="home-shortcut-card action" onClick={() => setModalShowParceiro(true)} type="button">
                                        <span className="home-shortcut-icon"><FaChartBar /></span>
                                        <div>
                                            <strong>Relatorio parceiros</strong>
                                            <p>Consolidar ponto, salas e valores utilizados.</p>
                                        </div>
                                        <FaArrowRight className="home-shortcut-arrow" />
                                    </button>
                                ) : null}
                            </div>
                        </Card.Body>
                    </Card>
                </div>
            </div>

            <Modal className="portal-modal" show={modalShowPaciente} onHide={() => setModalShowPaciente(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Filtros do relatorio de pacientes</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="portal-form-grid two-columns">
                        <Form.Group>
                            <Form.Label>De</Form.Label>
                            <Form.Control
                                type="date"
                                value={filters.dataDe}
                                onChange={(event) => setFilters({ ...filters, dataDe: event.target.value })}
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>Ate</Form.Label>
                            <Form.Control
                                type="date"
                                value={filters.dataAte}
                                onChange={(event) => setFilters({ ...filters, dataAte: event.target.value })}
                            />
                        </Form.Group>
                    </div>
                    <Form.Group className="mt-3">
                        <Form.Label>Colaborador</Form.Label>
                        <Form.Select
                            value={filters.idParceiro}
                            onChange={(event) => setFilters({ ...filters, idParceiro: event.target.value })}
                        >
                            <option value="">Todos</option>
                            {partnerList.map((partner) => (
                                <option key={partner.idUsuario} value={partner.idUsuario}>
                                    {`${partner.Nome} ${partner.Sobrenome}`}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setModalShowPaciente(false)}>
                        Sair
                    </Button>
                    <Button variant="danger" onClick={cleanFilters}>
                        Limpar
                    </Button>
                    <Button variant="success" onClick={generateReportPatient}>
                        Exportar
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal className="portal-modal" show={modalShowParceiro} onHide={() => setModalShowParceiro(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Filtros do relatorio de parceiros</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="portal-form-grid two-columns">
                        <Form.Group>
                            <Form.Label>De</Form.Label>
                            <Form.Control
                                type="date"
                                value={filters.dataDe}
                                onChange={(event) => setFilters({ ...filters, dataDe: event.target.value })}
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>Ate</Form.Label>
                            <Form.Control
                                type="date"
                                value={filters.dataAte}
                                onChange={(event) => setFilters({ ...filters, dataAte: event.target.value })}
                            />
                        </Form.Group>
                    </div>
                    <Form.Group className="mt-3">
                        <Form.Label>Colaborador</Form.Label>
                        <Form.Select
                            value={filters.idParceiro}
                            onChange={(event) => setFilters({ ...filters, idParceiro: event.target.value })}
                        >
                            <option value="">Todos</option>
                            {partnerList.map((partner) => (
                                <option key={partner.idUsuario} value={partner.idUsuario}>
                                    {`${partner.Nome} ${partner.Sobrenome}`}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setModalShowParceiro(false)}>
                        Sair
                    </Button>
                    <Button variant="danger" onClick={cleanFilters}>
                        Limpar
                    </Button>
                    <Button variant="success" onClick={generateReportPartner}>
                        Exportar
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default Home;
